<template>
  <div class="asc-customAlerts-threshold--container">
    <asc-required-label
      class="asc-customAlerts-threshold--title"
      label="SET RANGE"
    ></asc-required-label>
    <p>
      Enter a value in the Range Min and Range Max fields to see it reflected in
      the histogram above. To activate the range values in your custom analytic
      triggering logic, keep the boxes checked.
    </p>

    <div class="asc-customAlerts-threshold--rangeContainer">
      <div class="asc-customAlerts-threshold--range">
        <div class="rangeCheckbox">
          <asc-checkbox
            :checked="isRangeMinEnabled"
            :disabled="minRange === null || minRange === undefined"
            @change="handleActivateMinRange"
          >
          </asc-checkbox>
        </div>
        <asc-text-field-input
          label="Range Min"
          class="rangeTextField"
          prefix=">"
          :error="isMinRangeInvalid"
          type="number"
          :error-messages="
            minRange && isMinRangeInvalid
              ? 'Please enter a valid unit value'
              : !isNumberMinRange
              ? 'Must be a numeric value.'
              : ''
          "
          :value="minRange"
          @keypress.native="isNumber($event, 'isMinRange')"
          @paste.native="onPaste($event, 'isMinRange')"
          @blur="updateMinRange"
        />
      </div>
      <div class="asc-customAlerts-threshold--range">
        <div class="rangeCheckbox">
          <asc-checkbox
            :checked="isRangeMaxEnabled"
            :disabled="maxRange === null || maxRange === undefined"
            @change="handleActivateMaxRange"
          >
          </asc-checkbox>
        </div>
        <asc-text-field-input
          label="Range Max"
          class="rangeTextField"
          prefix="<"
          :value="maxRange"
          :error-messages="
            maxRange && isMaxRangeInvalid
              ? 'Please enter a valid unit value'
              : !isNumberMaxRange
              ? 'Must be a numeric value.'
              : ''
          "
          :error="isMaxRangeInvalid"
          @keypress.native="isNumber($event, 'isMaxRange')"
          @paste.native="onPaste($event, 'isMaxRange')"
          @blur="updateMaxRange"
        />
      </div>
    </div>
    <div class="mt-4">
      <asc-required-label
        label="SET THRESHOLD"
        required
        class="asc-customAlerts-threshold--title"
      />
    </div>
    <asc-validation-message
      v-if="isThresholdAlertActive"
      :show="true"
      message="Please select at least one threshold."
    ></asc-validation-message>
    <p>
      Enter a value into the fields to see it represented in the histogram
      above. To activate the thresholds in your custom analytic triggering
      logic, keep the boxes checked. Set at least one threshold.
    </p>
    <div class="asc-customAlerts-threshold--thresholdContainer">
      <div class="asc-customAlerts-threshold--range">
        <div class="rangeCheckbox">
          <asc-checkbox
            :checked="isLowWarningEnabled"
            :disabled="lowWarning === null || lowWarning === undefined"
            @change="handleActivateLowWarning"
          >
          </asc-checkbox>
        </div>
        <asc-text-field-input
          label="Low Warning"
          label-icon="lowAlarm"
          prefix="<"
          class="rangeTextField"
          type="number"
          :value="lowWarning"
          :error-messages="
            lowWarning && isLowWarningInvalid
              ? 'Please enter a valid unit value'
              : !isNumberLowWarning
              ? 'Must be a numeric value.'
              : ''
          "
          :error="isLowWarningInvalid"
          @keypress.native="isNumber($event, 'isLowWarning')"
          @paste.native="onPaste($event, 'isLowWarning')"
          @blur="updateLowWarning"
        />
      </div>
      <div class="asc-customAlerts-threshold--range">
        <div class="rangeCheckbox">
          <asc-checkbox
            :checked="isLowCautionEnabled"
            :disabled="lowCaution === null || lowCaution === undefined"
            @change="handleActivateLowCaution"
          >
          </asc-checkbox>
        </div>
        <asc-text-field-input
          label="Low Caution"
          label-icon="lowWarning"
          prefix="<"
          class="rangeTextField"
          type="number"
          :value="lowCaution"
          :error-messages="
            lowCaution && isLowCautionInvalid
              ? 'Please enter a valid unit value'
              : !isNumberLowCaution
              ? 'Must be a numeric value.'
              : ''
          "
          :error="isLowCautionInvalid"
          @keypress.native="isNumber($event, 'isLowCaution')"
          @paste.native="onPaste($event, 'isLowCaution')"
          @blur="updateLowCaution"
        />
      </div>
      <div class="asc-customAlerts-threshold--range">
        <div class="rangeCheckbox">
          <asc-checkbox
            :checked="isHighCautionEnabled"
            :disabled="highCaution === null || highCaution === undefined"
            @change="handleActivateHighCaution"
          >
          </asc-checkbox>
        </div>
        <asc-text-field-input
          label="High Caution"
          label-icon="highWarning"
          prefix=">"
          class="rangeTextField"
          type="number"
          :value="highCaution"
          :error-messages="
            highCaution && isHighCautionInvalid
              ? 'Please enter a valid unit value'
              : !isNumberHighCaution
              ? 'Must be a numeric value.'
              : ''
          "
          :error="isHighCautionInvalid"
          @keypress.native="isNumber($event, 'isHighCaution')"
          @paste.native="onPaste($event, 'isHighCaution')"
          @blur="updateHighCaution"
        />
      </div>
      <div class="asc-customAlerts-threshold--range">
        <div class="rangeCheckbox">
          <asc-checkbox
            :checked="isHighWarningEnabled"
            :disabled="highWarning === null || highWarning === undefined"
            @change="handleActivateHighWarning"
          >
          </asc-checkbox>
        </div>
        <asc-text-field-input
          label="High Warning"
          label-icon="highAlarm"
          prefix=">"
          class="rangeTextField"
          type="number"
          :value="highWarning"
          :error-messages="
            highWarning && isHighWarningInvalid
              ? 'Please enter a valid unit value'
              : !isNumberHighWarning
              ? 'Must be a numeric value.'
              : ''
          "
          :error="isHighWarningInvalid"
          @keypress.native="isNumber($event, 'isHighWarning')"
          @paste.native="onPaste($event, 'isHighWarning')"
          @blur="updateHighWarning"
        />
      </div>
    </div>
  </div>
</template>

<script>
import AscCheckbox from '@/components/atoms/AscCheckbox'
import AscTextFieldInput from '@/components/atoms/AscTextFieldInput'
import AscRequiredLabel from '@/components/atoms/AscRequiredLabel'
import _isFinite from 'lodash/isFinite'
import _toNumber from 'lodash/toNumber'
import AscValidationMessage from '@/components/atoms/AscValidationMessage'

export default {
  name: 'AscCustomAlertsThresholdWrapper',

  components: {
    AscCheckbox,
    AscTextFieldInput,
    AscRequiredLabel,
    AscValidationMessage,
  },

  props: {
    minRange: {
      type: [Number],
      default: undefined,
    },
    maxRange: {
      type: [Number],
      default: undefined,
    },
    lowWarning: {
      type: [Number],
      default: undefined,
    },
    lowCaution: {
      type: [Number],
      default: undefined,
    },
    highWarning: {
      type: [Number],
      default: undefined,
    },
    highCaution: {
      type: [Number],
      default: undefined,
    },
    isRangeMinEnabled: {
      type: Boolean,
    },
    isRangeMaxEnabled: {
      type: Boolean,
    },
    isLowWarningEnabled: {
      type: Boolean,
    },
    isHighWarningEnabled: {
      type: Boolean,
    },
    isLowCautionEnabled: {
      type: Boolean,
    },
    isHighCautionEnabled: {
      type: Boolean,
    },
    isMinRangeInvalid: {
      type: Boolean,
    },
    isMaxRangeInvalid: {
      type: Boolean,
    },
    isLowWarningInvalid: {
      type: Boolean,
    },
    isHighWarningInvalid: {
      type: Boolean,
    },
    isLowCautionInvalid: {
      type: Boolean,
    },
    isHighCautionInvalid: {
      type: Boolean,
    },
    isThresholdAlertActive: {
      type: Boolean,
      default: null,
    },
  },

  data() {
    return {
      firstTimeMinRangeEdit: true,
      firstTimeMaxRangeEdit: true,
      firstTimeHighWarningEdit: true,
      firstTimeHighCautionEdit: true,
      firstTimeLowWarningEdit: true,
      firstTimeLowCautionEdit: true,

      isNumberMinRange: true,
      isNumberMaxRange: true,
      isNumberHighWarning: true,
      isNumberHighCaution: true,
      isNumberLowWarning: true,
      isNumberLowCaution: true,
    }
  },
  computed: {
    isThresholdAlertActiveChild() {
      return (
        this.isHighCautionEnabled ||
        this.isHighWarningEnabled ||
        this.isLowWarningEnabled ||
        this.isLowCautionEnabled
      )
    },
  },

  methods: {
    emitThresholdAlertStatus() {
      this.$emit('updateThresholdAlertStatus', this.isThresholdAlertActiveChild)
    },
    handleActivateMaxRange(value) {
      this.$emit('updateMaxRange', {
        active: value,
        value: this.maxRange,
        valid: !this.isMaxRangeInvalid,
      })
      this.emitThresholdAlertStatus()
    },
    handleActivateMinRange(value) {
      this.$emit('updateMinRange', {
        active: value,
        value: this.minRange,
        valid: !this.isMinRangeInvalid,
      })
      this.emitThresholdAlertStatus()
    },
    handleActivateHighWarning(value) {
      this.$emit('updateHighWarning', {
        active: value,
        value: this.highWarning,
        valid: !this.isHighWarningInvalid,
      })
      this.emitThresholdAlertStatus()
    },
    handleActivateLowWarning(value) {
      this.$emit('updateLowWarning', {
        active: value,
        value: this.lowWarning,
        valid: !this.isLowWarningInvalid,
      })
      this.emitThresholdAlertStatus()
    },
    handleActivateLowCaution(value) {
      this.$emit('updateLowCaution', {
        active: value,
        value: this.lowCaution,
        valid: !this.isLowCautionInvalid,
      })
      this.emitThresholdAlertStatus()
    },
    handleActivateHighCaution(value) {
      this.$emit('updateHighCaution', {
        active: value,
        value: this.highCaution,
        valid: !this.isHighCautionInvalid,
      })
      this.emitThresholdAlertStatus()
    },
    updateMinRange(value) {
      let emittedValue = value
      if (_isFinite(_toNumber(emittedValue))) {
        this.isNumberMinRange = true
      }
      let active = this.isRangeMinEnabled
      if (emittedValue === '' || emittedValue === null) {
        emittedValue = null
        active = false
      } else if (isNaN(parseFloat(emittedValue))) {
        emittedValue = value
      } else {
        emittedValue = parseFloat(emittedValue)
        active = !active && this.firstTimeMinRangeEdit ? true : active
      }
      this.$emit('updateMinRange', {
        active: active,
        value: emittedValue,
        valid: !this.isMinRangeInvalid,
      })
      this.firstTimeMinRangeEdit = false
    },
    updateMaxRange(value) {
      let emittedValue = value
      if (_isFinite(_toNumber(emittedValue))) {
        this.isNumberMaxRange = true
      }
      let active = this.isRangeMaxEnabled
      if (emittedValue === '' || emittedValue === null) {
        emittedValue = null
        active = false
      } else if (isNaN(parseFloat(emittedValue))) {
        emittedValue = value
      } else {
        emittedValue = parseFloat(emittedValue)
        active = !active && this.firstTimeMaxRangeEdit ? true : active
      }
      this.$emit('updateMaxRange', {
        active: active,
        value: emittedValue,
        valid: !this.isMaxRangeInvalid,
      })
      this.firstTimeMaxRangeEdit = false
    },
    updateLowWarning(value) {
      let emittedValue = value
      if (_isFinite(_toNumber(emittedValue))) {
        this.isNumberLowWarning = true
      }
      let active = this.isLowWarningEnabled
      if (emittedValue === '' || emittedValue === null) {
        emittedValue = null
        active = false
      } else if (isNaN(parseFloat(emittedValue))) {
        emittedValue = value
      } else {
        emittedValue = parseFloat(emittedValue)
        active = !active && this.firstTimeLowWarningEdit ? true : active
      }
      this.$emit('updateLowWarning', {
        active: active,
        value: emittedValue,
        valid: !this.isLowWarningInvalid,
      })
      this.firstTimeLowWarningEdit = false
    },
    updateLowCaution(value) {
      let emittedValue = value
      if (_isFinite(_toNumber(emittedValue))) {
        this.isNumberLowCaution = true
      }
      let active = this.isLowCautionEnabled
      if (emittedValue === '' || emittedValue === null) {
        emittedValue = null
        active = false
      } else if (isNaN(parseFloat(emittedValue))) {
        emittedValue = value
      } else {
        emittedValue = parseFloat(emittedValue)
        active = !active && this.firstTimeLowCautionEdit ? true : active
      }
      this.$emit('updateLowCaution', {
        active: active,
        value: emittedValue,
        valid: !this.isLowCautionInvalid,
      })
      this.firstTimeLowCautionEdit = false
    },
    updateHighCaution(value) {
      let emittedValue = value
      if (_isFinite(_toNumber(emittedValue))) {
        this.isNumberHighCaution = true
      }
      let active = this.isHighCautionEnabled
      if (emittedValue === '' || emittedValue === null) {
        emittedValue = null
        active = false
      } else if (isNaN(parseFloat(emittedValue))) {
        emittedValue = value
      } else {
        emittedValue = parseFloat(emittedValue)
        active = !active && this.firstTimeHighCautionEdit ? true : active
      }
      this.$emit('updateHighCaution', {
        active: active,
        value: emittedValue,
        valid: !this.isHighCautionInvalid,
      })
      this.firstTimeHighCautionEdit = false
    },
    updateHighWarning(value) {
      let emittedValue = value
      if (_isFinite(_toNumber(emittedValue))) {
        this.isNumberHighWarning = true
      }
      let active = this.isHighWarningEnabled
      if (emittedValue === '' || emittedValue === null) {
        emittedValue = null
        active = false
      } else if (isNaN(parseFloat(emittedValue))) {
        emittedValue = value
      } else {
        emittedValue = parseFloat(emittedValue)
        active = !active && this.firstTimeHighWarningEdit ? true : active
      }
      this.$emit('updateHighWarning', {
        active: active,
        value: emittedValue,
        valid: !this.isHighWarningInvalid,
      })
      this.firstTimeHighWarningEdit = false
    },

    validateNumber(input, valid) {
      switch (input) {
        case 'isMinRange':
          this.isNumberMinRange = valid
          break
        case 'isMaxRange':
          this.isNumberMaxRange = valid
          break
        case 'isLowWarning':
          this.isNumberLowWarning = valid
          break
        case 'isHighWarning':
          this.isNumberHighWarning = valid
          break
        case 'isLowCaution':
          this.isNumberLowCaution = valid
          break
        case 'isHighCaution':
          this.isNumberHighCaution = valid
          break
      }
    },

    isNumber(event, input) {
      event = event ? event : window.event
      var charCode = event.which ? event.which : event.keyCode

      if (
        charCode > 31 &&
        (charCode < 48 || charCode > 57) &&
        charCode !== 46
      ) {
        this.validateNumber(input, false)
        event.preventDefault()
      } else {
        this.validateNumber(input, true)
        const inputValue = event.target.value
        const numberOfPeriods = inputValue.split('.').length
        if (numberOfPeriods > 1 && charCode === 46) {
          event.preventDefault()
          return
        }
        return true
      }
    },

    onPaste(event, input) {
      const value = event.clipboardData.getData('text')
      const valid = _isFinite(_toNumber(value))
      this.validateNumber(input, valid)

      if (!valid) {
        event.preventDefault()
        return false
      }
    },
  },
}
</script>

<style lang="scss">
.asc-customAlerts-threshold--container {
  padding: 16px;

  .asc-customAlerts-threshold--title {
    margin-bottom: 15px;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 1px;
    font-weight: 500;
  }

  .asc-customAlerts-threshold--rangeContainer,
  .asc-customAlerts-threshold--thresholdContainer {
    display: flex;
    margin-top: 20px;

    .rangeCheckbox {
      .asc-checkbox {
        display: flex;
        height: 15px;
        margin-top: 5px;
        width: 22px;
        position: relative;
      }
    }

    .asc-customAlerts-threshold--range {
      flex: 1;
      padding-right: 24px;
      position: relative;
      display: flex;
      align-items: flex-start;

      .rangeCheckbox {
        flex: 1;
        max-width: 30px;
      }

      .rangeTextField {
        flex: 3;

        .v-text-field {
          margin-top: 2px;
        }
      }
    }
  }
}
</style>
